import { useEffect, useMemo, useState } from 'react'
import { useGetCurrentGame, useGetCurrentLanguage } from 'src/store/game'
import { isMarbleGame } from 'src/types/domain/Game'
import { useClientSideTranslation } from 'src/services/i18n/client'
import { getTranslatedGameTitle } from 'src/services/i18n/utils/gameTranslations'
import { isRaceSettled } from 'src/utils/raceUtils'
import { isBirdieGameType, isCrashGameType } from 'src/utils/gameTypeUtils'
import { useVisualCrashStatus } from 'src/components/CrashChipPanel/useVisualCrash'
import { resolveCrashEventInfo } from 'src/components/CrashChipPanel/crashEventInfo'
import {
  CrashRoundModal,
  hasRoundFairness,
  resolveModalRace,
  selectRoundFairness
} from 'src/components/CrashMultiplierHistory'
import type { Race } from 'src/types/domain/Race'
import { getRaceRoundId } from 'src/utils/getRaceRoundId'
import { mergeRaceHistory } from 'src/utils/mergeRaceHistory'
import { useDeepRaceHistory } from 'src/hooks/useDeepRaceHistory'
import { HISTORY } from 'src/config'
import { BirdieRoundRows } from 'src/components/BirdieBoard/BirdieRoundRows'
import { PastRace } from './components/PastRace'
import { RoundStats } from './components/RoundStats'

/** Stable identity so the merge memo below isn't defeated for a non-marble game. */
const NO_RACES: Race[] = []

export function PastRacesSidebar() {
  const game = useGetCurrentGame()
  const { t } = useClientSideTranslation(useGetCurrentLanguage())
  // A tapped past-round row opens the same provably-fair modal the history
  // pills open — for any round that published a commitment or a reveal (crash
  // or counting), never gated on the game family. The modal renders only
  // while the id still resolves against the store (rounds get pruned).
  const [selectedRaceId, setSelectedRaceId] = useState<string | null>(null)
  // v49 — ANTI-SPOILER (same v39 hold the history pills wear): the newest
  // round settles on the EVENT clock, which leads the viewer's frames, so
  // with this panel open its row — wearing the crash multiplier — used to
  // file in ~2s before the bust was visible on screen. While the latest
  // round's crash is known-but-unseen ('armed'), hold its row out.
  const visualCrashStatus = useVisualCrashStatus()

  // The store only ever holds ~10 rounds and re-seeds itself on every tab
  // refocus, so the deeper window comes from one lazy read taken when this
  // panel mounts and kept in local state. See useDeepRaceHistory for why it
  // must not go through the store.
  const gameId = isMarbleGame(game) ? game.id : undefined
  const { races: deepRaces, isLoading: isLoadingHistory } = useDeepRaceHistory(gameId)
  const [visibleCount, setVisibleCount] = useState(HISTORY.ROUNDS_VISIBLE)

  useEffect(() => {
    setVisibleCount(HISTORY.ROUNDS_VISIBLE)
  }, [gameId])

  // The whole window: store rounds (live, socket-fed) merged over the deep
  // read. `game.races` comes from a createSelector, so its identity is stable
  // between unrelated store ticks — which is what lets these memos actually
  // hold and keeps the 200-round scans off every render.
  const storeRaces = isMarbleGame(game) ? game.races : NO_RACES
  const isCrashGame = isMarbleGame(game) && isCrashGameType(game.track.gameType)
  // B19b — the golf game's ROW, and nothing else about this panel. Owner: "use
  // what we already have in the sidebar but just make a specific version for
  // birdie time so if this golf it will show that else fallback to the current".
  // The chassis, the header, the close, the deep read, the paging, the loading
  // and the empty state below are the shared ones, unchanged; a Birdie game
  // renders the page's rounds as compact round cards instead of `<PastRace>`.
  const isBirdieGame = isMarbleGame(game) && isBirdieGameType(game.track.gameType)
  const allRaces = useMemo(
    () => mergeRaceHistory(storeRaces, deepRaces),
    [storeRaces, deepRaces]
  )

  // Anchored on the MERGED window's newest round, not the store's: the deep
  // read can surface a settled round the store never received, and anchoring
  // on the store would let that round's multiplier through the hold.
  const latestCrashRace = isCrashGame ? allRaces[0] : undefined
  const holdNewestId =
    latestCrashRace &&
    visualCrashStatus === 'armed' &&
    resolveCrashEventInfo(latestCrashRace.raceEvents).crashKnown
      ? latestCrashRace.uuid
      : null

  // The stats read the WHOLE settled window; the list shows a page of it.
  const settledRaces = useMemo(
    () => allRaces.filter(isRaceSettled).filter((race) => race.uuid !== holdNewestId),
    [allRaces, holdNewestId]
  )

  if (!isMarbleGame(game)) {
    return null
  }

  const races = settledRaces.slice(0, visibleCount)
  const hasMore = settledRaces.length > races.length

  // Note 1 — the CURRENT round shown as an "in progress" entry, but ONLY while
  // its outcome is not yet known (crashKnown === false ⇒ no multiplier can leak,
  // so this is fully spoiler-safe). This lets a player open the round the moment
  // it opens and see the commitment BEFORE betting — the whole point of a
  // commitment — instead of only after settlement. The modal renders
  // commitment-only for an unsettled round (no derivation/verdict yet).
  //
  // Gated on the ROUND carrying a commitment, not on the game family: counting
  // (CCTV) rounds publish the same pre-round commitment and deserve the same
  // affordance, and a game whose engine publishes nothing still shows no row.
  //
  // B19b — NOT on the golf game. The row is drawn in the crash compact dress
  // (`past-race-compact--crash`, the 50-px two-line "In progress" / round id /
  // committed row) and opens the generic round reader, and a Birdie round
  // publishes a commitment like every other, so it rendered for most of every
  // betting window: one foreign 50-px row sitting on top of 170-px Birdie cards,
  // opening the wrong panel. The owner's rule is the Birdie drawing or the
  // shared one, never a mix, and Birdie has no in-progress row of its own yet —
  // so it shows none, and its open round is read from the board itself.
  // Gated on the VALUE rather than at the JSX, so the empty state below (which
  // suppresses itself while an in-progress row is standing) stays correct.
  const currentRace = allRaces[0]
  const inProgressRace =
    !isBirdieGame &&
    currentRace &&
    !isRaceSettled(currentRace) &&
    !resolveCrashEventInfo(currentRace.raceEvents).crashKnown &&
    selectRoundFairness(currentRace).commitment
      ? currentRace
      : undefined

  // Resolve against the MERGED window, not the store. The list renders up to
  // 200 rows while the store holds ~10, so resolving against the store made
  // every row past the 10th a visibly-tappable dead end — it set the id, found
  // nothing, and silently rendered no modal.
  //
  // ALL races, not just the settled slice, so the in-progress round opens too.
  // Un-gated: the id can only have been set by a row that had a commitment or
  // a reveal to show.
  const modalRace = resolveModalRace(allRaces, selectedRaceId)

  return (
    <div className="past-races-sidebar">
      <h3>{getTranslatedGameTitle(game.track, t)}</h3>
      <RoundStats game={game} isLoading={isLoadingHistory} races={settledRaces} />
      <div className="std-table two-rows-multiple-columns">
        <div className="std-table__inner">
          <div className="std-table__inner-body no-max-height">
            {inProgressRace && (
              <div
                className="past-race-compact past-race-compact--crash past-race-compact--tappable past-race-compact--in-progress"
                role="button"
                tabIndex={0}
                aria-label={`${t('Round details')}: ${t('In progress')}`}
                onClick={() => setSelectedRaceId(inProgressRace.uuid)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedRaceId(inProgressRace.uuid)
                  }
                }}
              >
                <div className="past-race-compact__primary">
                  <span className="past-race-compact__crash">⏳ {t('In progress')}</span>
                </div>
                <div className="past-race-compact__footer">
                  <span className="past-race-compact__footer-meta">
                    <span className="past-race-compact__round-id">
                      {getRaceRoundId(inProgressRace.round, game.track.shortName)}
                    </span>
                  </span>
                  <span className="past-race-compact__time">{t('committed')}</span>
                </div>
              </div>
            )}
            {races.length > 0 ? (
              isBirdieGame ? (
                <BirdieRoundRows marbles={game.marbles} races={races} />
              ) : (
                races.map((race) => (
                  <PastRace
                    game={game}
                    key={race.uuid}
                    race={race}
                    onOpenFairness={
                      hasRoundFairness(race) ? () => setSelectedRaceId(race.uuid) : undefined
                    }
                  />
                ))
              )
            ) : inProgressRace ? null : (
              <div className="entry entry--empty">
                <span>{t('No Ended Races')}</span>
              </div>
            )}
            {hasMore && (
              <button
                className="past-races-sidebar__more"
                type="button"
                onClick={() => setVisibleCount((count) => count + HISTORY.ROUNDS_STEP)}
              >
                {t('Show more')}
              </button>
            )}
          </div>
        </div>
      </div>
      {modalRace && <CrashRoundModal race={modalRace} onClose={() => setSelectedRaceId(null)} />}
    </div>
  )
}
